import React from 'react';
import type { EventShowPeriodSummary } from '../../data/showPeriodSummary';
import { formatShowWallTime } from '../../domain/showPeriodTime';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Link, useNavigate } from 'react-router-dom';
import { deriveEventCalendarDateRange, eventCustomerTotalAmount, type EventListRow } from '../../data/events';
import type { OverdueMap } from '../../data/overdue';
import type { EventAvailability } from '../../domain/events/useEventAvailability';
import { eventDisplayName } from '../../domain/events';
import { overdueTooltip, urgentTooltip } from '../../data/overdue';
import { invoiceReviewHoldMessage, updateEventStageStatus } from '../../data/orderStages';
import { fetchLinkedFulfillmentOrderCounts } from '../../data/orderWorkspace';
import { useOrderStageReference } from '../../hooks/useOrderStageReference';
import { deriveOrderStage, formatOrderStatusLabel } from '../../lib/orderStages';
import {
  canOpenEventFulfillmentScanner,
  defaultEventFulfillmentScanMode,
} from '../../domain/fulfillment/eventFulfillmentScanMode';
import EventFlags from '../EventFlags';
import EventConfirmationModal from '../event/EventConfirmationModal';
import { LibraryViewToggle } from '../../ui/LibraryViewToggle';
import useLibraryPhoneLayout from '../../hooks/useLibraryPhoneLayout';
import MenuButton, { type MenuButtonItem } from '../common/MenuButton';
import './EventsCalendarView.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CalendarMode = 'month' | 'week' | 'day';
type CalendarStageUpdatePayload = {
  eventId: string;
  status: string;
  reservationJobStatus?: string | null;
};
type CalendarStageLegendItem = {
  status: string;
  label: string;
};

interface EventsCalendarViewProps {
  events: EventListRow[];
  showPeriodSummaries?: Record<string, EventShowPeriodSummary>;
  overdueMap?: OverdueMap;
  /** Called when the visible date window changes (anchor/mode navigation). */
  onDateRangeChange?: (start: string, end: string) => void;
  /** Availability data per event id, used for icon mode in calendar. */
  availabilityMap?: Map<string, EventAvailability>;
  /** Current availability display mode. */
  availabilityMode?: 'off' | 'glow' | 'bar' | 'icon';
  /** Whether right-click context menu is enabled (true for managers/admins). */
  isActionable?: boolean;
  /** Delete handler for context menu. */
  onDelete?: (eventId: string, name: string | null) => void;
  /** Called after a stage update so parent can refetch. */
  onStageUpdated?: (payload?: CalendarStageUpdatePayload) => void;
  /** Begin picking handler for context menu. */
  onBeginPicking?: (eventId: string) => void;
  /** Ordered event stage reference from the Events library filters. */
  stageLegendItems?: CalendarStageLegendItem[];
  /** View-level toggle for warehouse prep/ship/return whiskers. */
  showWarehouseWhiskers?: boolean;
  /** Called when the calendar toolbar warehouse whisker toggle changes. */
  onShowWarehouseWhiskersChange?: (checked: boolean) => void;
  /** Whether hidden event calendar rows should be temporarily revealed for this viewer. */
  showHiddenEvents?: boolean;
  /** Called when the manager/admin temporary hidden-event reveal toggle changes. */
  onShowHiddenEventsChange?: (checked: boolean) => void;
  /** Number of hidden events in the current calendar window, shown while reveal is off. */
  hiddenEventCount?: number;
  /** Whether the current viewer may temporarily reveal hidden events. */
  canRevealHiddenEvents?: boolean;
}

interface CalendarEvent {
  id: string;
  rowKey: string;
  periodLabel?: string;
  betweenShows?: boolean;
  name: string;
  code: string;
  status: string | null;
  customerPickup: boolean;
  /** Core on-site window: load-in through pickup when set, then event/show dates. */
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  /** Earliest warehouse logistics date (fill or ship), if before the on-site window. */
  whiskStart: dayjs.Dayjs | null;
  /** Latest warehouse logistics date (return), if after the on-site window. */
  whiskEnd: dayjs.Dayjs | null;
  venueName: string | null;
  customerName: string | null;
  pmName: string | null;
  spName: string | null;
  customerTotalAmount: number | null | undefined;
  itemCount: number;
  raw: EventListRow;
}

interface LaneEvent extends CalendarEvent {
  lane: number;
  colorIndex: number;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const EVENT_COLORS = [
  '#2563EB', // blue
  '#DC2626', // red
  '#059669', // emerald
  '#D97706', // amber
  '#7C3AED', // violet
  '#DB2777', // pink
  '#0891B2', // cyan
  '#CA8A04', // yellow
  '#4F46E5', // indigo
  '#E11D48', // rose
  '#0D9488', // teal
  '#EA580C', // orange
  '#6D28D9', // purple
  '#0284C7', // sky
  '#65A30D', // lime
  '#BE185D', // fuchsia
  '#B45309', // dark amber
  '#047857', // dark emerald
  '#6366F1', // soft indigo
  '#9333EA', // bright purple
  '#0E7490', // dark cyan
  '#C2410C', // burnt orange
  '#1D4ED8', // royal blue
  '#A21CAF', // magenta
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function eventStartDate(ev: EventListRow): string | null {
  return deriveEventCalendarDateRange(ev).startDate;
}

function eventEndDate(ev: EventListRow): string | null {
  return deriveEventCalendarDateRange(ev).endDate;
}

function primaryVenueName(ev: EventListRow): string | null {
  try {
    return ev.event_venues?.[0]?.venues?.venue_name || null;
  } catch {
    return null;
  }
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatStatusLabel(status: string | null): string {
  if (!status) return '--';
  if (status === 'lost' || status === 'void') return 'Lost';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function availColorClass(avail: EventAvailability | undefined): string {
  if (!avail || avail.ratio < 0) return '';
  if (avail.ratio >= 1) return avail.returnWarning ? 'yellow' : 'green';
  if (avail.ratio > 0.5) return 'yellow';
  return 'red';
}

/**
 * Map each event stage to a small icon (inline SVG).
 * Icons are white with slight transparency so they sit on colored bar backgrounds.
 */
const STAGE_ICONS: Record<string, React.ReactNode> = {
  // Quote -- document with lines
  quote: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="1" width="10" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="5.5" y1="5" x2="10.5" y2="5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="5.5" y1="8" x2="9" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  ),
  // Budgetary -- document with dotted lines
  budgetary: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="1" width="10" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="5.5" y1="5" x2="10.5" y2="5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="2 2"/>
      <line x1="5.5" y1="8" x2="9" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="2 2"/>
    </svg>
  ),
  // Approval pending -- hourglass
  approval_pending: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 2h6v3.5L8 8l3 2.5V14H5v-3.5L8 8 5 5.5V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  ),
  // Approved -- thumbs up
  approved: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 7.5l2.5-4.5 1.5 0 -.5 3H12.5l-1.5 6H5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
      <line x1="3" y1="7.5" x2="3" y2="14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  // Pending acceptance -- delivered envelope awaiting client action
  pending_acceptance: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M3 5l5 4 5-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Confirmed -- checkmark in circle
  confirmed: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
      <polyline points="5.5,8 7.5,10.5 11,5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Picking -- hand reaching into box
  picking: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="6" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <polyline points="6,6 6,3 10,3 10,6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="8" y1="9" x2="8" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  ),
  // Partially filled -- box with partial progress
  partially_filled: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M2.8 7.3h10.4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      <path d="M5 10.2h3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M5 12h5.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity=".55"/>
    </svg>
  ),
  // Filled -- box with check
  filled: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="5" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M2.8 7.3h10.4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      <polyline points="5.1,10.5 7,12.2 11,8.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Partially shipped -- truck with partial progress
  partially_shipped: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="4" width="8.2" height="7" rx="1" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M9.2 6h2.6l2.2 3v2H9.2" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
      <circle cx="4.2" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.2"/>
      <circle cx="11.8" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M3.5 7.5h3.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M3.5 9.4h2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".55"/>
    </svg>
  ),
  // Shipped -- truck
  shipped: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="4" width="9" height="7" rx="1" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M10 6h2.5l2 3v2h-4.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
      <circle cx="4.5" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.2"/>
      <circle cx="12" cy="12" r="1.3" stroke="currentColor" strokeWidth="1.2"/>
    </svg>
  ),
  // On rent -- play/in-progress (event is live)
  on_rent: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
      <polygon points="6.5,4.5 12,8 6.5,11.5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  // Returning -- arrow left
  returning: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polyline points="9,3 4,8 9,13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="4" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // Partially returned -- return arrow with partial progress
  partially_returned: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polyline points="9,3.5 4.5,8 9,12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="4.5" y1="8" x2="12.5" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M10.7 11.4h2.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M11.8 4.6h1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".55"/>
    </svg>
  ),
  // Returned -- box with check
  returned: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="4" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
      <polyline points="5.5,9 7.5,11.5 11,6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  // Ready to invoice -- dollar sign
  ready_to_invoice: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M5.5 5.5C5.5 4.4 6.6 3.5 8 3.5s2.5.9 2.5 2c0 1.5-2.5 1.5-2.5 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M10.5 10.5c0 1.1-1.1 2-2.5 2s-2.5-.9-2.5-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  // Invoiced -- receipt / document with dollar
  invoiced: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="1" width="10" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="8" y1="4" x2="8" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M6 6.5c0-.8.9-1.5 2-1.5s2 .7 2 1.5-1 1.2-2 1.5-2 .7-2 1.5.9 1.5 2 1.5 2-.7 2-1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),
  // Closed -- lock
  closed: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3.5" y="7" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5.5 7V5.5a2.5 2.5 0 015 0V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  // Cancelled -- X in circle
  cancelled: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="5.5" y1="5.5" x2="10.5" y2="10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="10.5" y1="5.5" x2="5.5" y2="10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  // Legacy void alias for Lost -- circle with slash
  void: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="3.5" y1="12.5" x2="12.5" y2="3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  // Lost -- circle with slash (alias)
  lost: (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="3.5" y1="12.5" x2="12.5" y2="3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
};

/** Fallback: generic circle for unknown stages */
const STAGE_ICON_FALLBACK = (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.4"/>
  </svg>
);

/** Ordered list of stages for the legend (matches typical event lifecycle). */
const DEFAULT_STAGE_LEGEND: { key: string; label: string }[] = [
  { key: 'quote', label: 'Quote' },
  { key: 'budgetary', label: 'Budgetary' },
  { key: 'approval_pending', label: 'Approval Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'pending_acceptance', label: 'Pending Acceptance' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'picking', label: 'Picking' },
  { key: 'partially_filled', label: 'Partially Filled' },
  { key: 'filled', label: 'Filled' },
  { key: 'partially_shipped', label: 'Partially Shipped' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'on_rent', label: 'On Rent' },
  { key: 'partially_returned', label: 'Partially Returned' },
  { key: 'returning', label: 'Returning' },
  { key: 'returned', label: 'Returned' },
  { key: 'ready_to_invoice', label: 'Ready to Invoice' },
  { key: 'closed', label: 'Closed' },
  { key: 'lost', label: 'Lost' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'cancelled', label: 'Cancelled' },
];

function buildStageLegend(items: CalendarStageLegendItem[] | undefined): { key: string; label: string }[] {
  if (!items || items.length === 0) return DEFAULT_STAGE_LEGEND;
  const seen = new Set<string>();
  const legend: { key: string; label: string }[] = [];
  for (const item of items) {
    const key = item.status.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    legend.push({ key, label: item.label.trim() || formatStatusLabel(key) });
  }
  return legend.length > 0 ? legend : DEFAULT_STAGE_LEGEND;
}

function StageIcon({ status }: { status: string | null }) {
  if (!status) return null;
  const key = status.trim().toLowerCase();
  const icon = STAGE_ICONS[key] ?? STAGE_ICON_FALLBACK;
  return (
    <span
      className="cal-bar__stage-icon"
      title={formatStatusLabel(status)}
      aria-label={formatStatusLabel(status)}
    >
      {icon}
    </span>
  );
}

/** Convert raw event rows into CalendarEvent objects, filtering out only those without usable dates. */
export function toCalendarEvents(events: EventListRow[], showWarehouseWhiskers = true, showHiddenEvents = false,
  summaries?: Record<string, EventShowPeriodSummary>): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  for (const ev of events) {
    if (ev.hide_from_event_calendar === true && !showHiddenEvents) continue;
    const summary = summaries?.[ev.id];
    if (summary?.advanced) {
      const date = (instant: string) => dayjs(formatShowWallTime(instant, summary.timezone).slice(0, 10));
      for (const period of summary.periods) {
        const start = date(period.setupAt), end = date(period.teardownAt);
        const prep = date(period.prepAt), returned = date(period.returnAt);
        const logistics = showWarehouseWhiskers && ev.hide_warehouse_tasks !== true;
        const label = period.label || `Show ${period.position}`;
        out.push({ id: ev.id, rowKey: `${ev.id}:${period.periodId}`, periodLabel: label, betweenShows: summary.betweenShows,
          name: `${eventDisplayName(ev.name, period.pickupMode === 'customer_pickup') || 'Untitled Event'} · ${label}`, code: ev.event_code ?? '', status: ev.status,
          customerPickup: period.pickupMode === 'customer_pickup', start, end,
          whiskStart: logistics && prep.isBefore(start) ? prep : null, whiskEnd: logistics && returned.isAfter(end) ? returned : null,
          venueName: primaryVenueName(ev), customerName: ev.client_display_name || ev.client_business || null,
          pmName: ev.project_manager_display_name || null, spName: ev.sales_manager_display_name || null,
          customerTotalAmount: eventCustomerTotalAmount(ev), itemCount: ev.item_count ?? 0, raw: ev });
      }
      continue;
    }
    const startStr = eventStartDate(ev);
    if (!startStr) continue;
    const start = dayjs(startStr);
    if (!start.isValid()) continue;
    const endStr = eventEndDate(ev);
    const end = endStr ? dayjs(endStr) : start;
    const eventStart = start.startOf('day');
    const eventEnd = (end.isValid() ? end : start).startOf('day');

    // Compute whisker dates from warehouse logistics fields
    let whiskStart: dayjs.Dayjs | null = null;
    let whiskEnd: dayjs.Dayjs | null = null;
    const warehouseTasksHidden = ev.hide_warehouse_tasks === true || !showWarehouseWhiskers;
    const fillD = !warehouseTasksHidden && ev.fill_date ? dayjs(ev.fill_date) : null;
    const shipD = !warehouseTasksHidden && ev.ship_date ? dayjs(ev.ship_date) : null;
    const retD = !warehouseTasksHidden && ev.return_date ? dayjs(ev.return_date) : null;

    // Earliest logistics date before event start
    const earlyDates = [fillD, shipD].filter(
      (d): d is dayjs.Dayjs => d != null && d.isValid() && d.isBefore(eventStart),
    );
    if (earlyDates.length > 0) {
      whiskStart = earlyDates.reduce((a, b) => (a.isBefore(b) ? a : b)).startOf('day');
    }

    // Latest logistics date after event end
    if (retD && retD.isValid() && retD.isAfter(eventEnd)) {
      whiskEnd = retD.startOf('day');
    }

    out.push({
      id: ev.id,
      rowKey: ev.id,
      name: eventDisplayName(ev.name, ev.customer_pickup === true) || 'Untitled Event',
      code: ev.event_code ?? '',
      status: ev.status,
      customerPickup: ev.customer_pickup === true,
      start: eventStart,
      end: eventEnd,
      whiskStart,
      whiskEnd,
      venueName: primaryVenueName(ev),
      customerName: ev.client_display_name || ev.client_business || null,
      pmName: ev.project_manager_display_name || null,
      spName: ev.sales_manager_display_name || null,
      customerTotalAmount: eventCustomerTotalAmount(ev),
      itemCount: ev.item_count ?? 0,
      raw: ev,
    });
  }
  return out;
}

/** Effective start of an event including its left whisker (prep/ship). */
function effectiveStart(ev: CalendarEvent): dayjs.Dayjs {
  return ev.whiskStart && ev.whiskStart.isBefore(ev.start) ? ev.whiskStart : ev.start;
}

/** Effective end of an event including its right whisker (return). */
function effectiveEnd(ev: CalendarEvent): dayjs.Dayjs {
  return ev.whiskEnd && ev.whiskEnd.isAfter(ev.end) ? ev.whiskEnd : ev.end;
}

/**
 * Assign lanes (vertical rows) to overlapping events using a greedy algorithm.
 * The full visual span (bar + whiskers) is used for overlap detection so that
 * whiskers never overlap another event's bar.
 * Colors are assigned sequentially so no two events in the visible set share a
 * color until the 24-color palette wraps.
 */
function assignLanesSimple(events: CalendarEvent[]): LaneEvent[] {
  const sorted = [...events].sort((a, b) => {
    const d = effectiveStart(a).diff(effectiveStart(b));
    if (d !== 0) return d;
    // longer events first so they get lower lanes
    return effectiveEnd(b).diff(effectiveStart(b)) - effectiveEnd(a).diff(effectiveStart(a));
  });

  // Each lane tracks the latest effective end date (bar + whiskers)
  const laneEnds: dayjs.Dayjs[] = [];
  const result: LaneEvent[] = [];
  const colorByEvent = new Map<string, number>();

  for (let i = 0; i < sorted.length; i++) {
    const ev = sorted[i];
    const evStart = effectiveStart(ev);
    const evEnd = effectiveEnd(ev);
    let chosenLane = -1;
    // Find first lane where this event (including whiskers) doesn't overlap
    for (let j = 0; j < laneEnds.length; j++) {
      if (evStart.isAfter(laneEnds[j])) {
        chosenLane = j;
        break;
      }
    }

    // Sequential color: each event gets a unique color index
    if (!colorByEvent.has(ev.id)) colorByEvent.set(ev.id, colorByEvent.size % EVENT_COLORS.length);
    const colorIndex = colorByEvent.get(ev.id)!;

    if (chosenLane >= 0) {
      laneEnds[chosenLane] = evEnd;
    } else {
      chosenLane = laneEnds.length;
      laneEnds.push(evEnd);
    }

    result.push({ ...ev, lane: chosenLane, colorIndex });
  }

  return result;
}

/** Get all weeks (rows) for a given month. Each week is an array of 7 dayjs objects. */
function getMonthWeeks(year: number, month: number): dayjs.Dayjs[][] {
  const first = dayjs().year(year).month(month).startOf('month');
  const startOfCalendar = first.startOf('week'); // Sunday
  const last = first.endOf('month');
  const endOfCalendar = last.endOf('week');

  const weeks: dayjs.Dayjs[][] = [];
  let cursor = startOfCalendar;
  while (cursor.isBefore(endOfCalendar) || cursor.isSame(endOfCalendar, 'day')) {
    const week: dayjs.Dayjs[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
}

/** Get 7 days of a week starting from the week containing `date`. */
function getWeekDays(date: dayjs.Dayjs): dayjs.Dayjs[] {
  const start = date.startOf('week');
  return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
}

/* ------------------------------------------------------------------ */
/*  Tooltip                                                            */
/* ------------------------------------------------------------------ */

interface TooltipState {
  event: CalendarEvent;
  x: number;
  y: number;
}

function EventTooltip({ event, x, y, overdueMap, availabilityMap }: TooltipState & { overdueMap?: OverdueMap; availabilityMap?: Map<string, EventAvailability> }) {
  const startStr = event.start.format('MMM D, YYYY');
  const endStr = event.end.format('MMM D, YYYY');
  const dateRange = event.start.isSame(event.end, 'day')
    ? startStr
    : `${startStr} - ${endStr}`;

  const overdueRow = overdueMap?.get(event.id) ?? null;
  const oTip = overdueRow ? overdueTooltip(overdueRow) : '';
  const uTip = overdueRow ? urgentTooltip(overdueRow) : '';
  const returnWarning = availabilityMap?.get(event.id)?.returnWarning;
  const timeError = availabilityMap?.get(event.id)?.timeError;

  const hasWarehouseDates = event.whiskStart || event.whiskEnd;

  // Position tooltip relative to viewport, nudging if it would overflow
  const style: React.CSSProperties = {
    position: 'fixed',
    left: x + 12,
    top: y + 12,
    zIndex: 9999,
  };

  return (
    <div className="cal-tooltip" style={style}>
      <div className="cal-tooltip__header">
        <span className="cal-tooltip__code">{event.code}</span>
        {event.status && (
          <span className="cal-tooltip__status pill subtle">
            {formatStatusLabel(event.status)}
          </span>
        )}
        {event.customerPickup ? <span className="customer-pickup-badge customer-pickup-badge--compact">Customer Pickup</span> : null}
        <EventFlags row={overdueRow} compact />
      </div>
      <div className="cal-tooltip__name">{event.name}</div>
      {event.betweenShows ? <div className="cal-tooltip__row">Between shows</div> : null}
      <div className="cal-tooltip__row">{dateRange}</div>
      {hasWarehouseDates && (
        <div className="cal-tooltip__row cal-tooltip__warehouse">
          {event.whiskStart && `Prep: ${event.whiskStart.format('MMM D')}`}
          {event.whiskStart && event.whiskEnd && ' \u00B7 '}
          {event.whiskEnd && `Return: ${event.whiskEnd.format('MMM D')}`}
        </div>
      )}
      {event.venueName && <div className="cal-tooltip__row">Venue: {event.venueName}</div>}
      {event.customerName && <div className="cal-tooltip__row">Customer: {event.customerName}</div>}
      <div className="cal-tooltip__row">
        PM: {event.pmName ?? '--'} | SP: {event.spName ?? '--'}
      </div>
      <div className="cal-tooltip__row">
        {event.periodLabel ? 'Event total: ' : ''}{formatCurrency(event.customerTotalAmount)}
        {event.itemCount > 0
          ? ` \u00B7 ${event.itemCount} item${event.itemCount !== 1 ? 's' : ''}`
          : ''}
      </div>
      {returnWarning ? <div className="cal-tooltip__row">{returnWarning}</div> : null}
      {timeError ? <div className="cal-tooltip__row">{timeError}</div> : null}
      {oTip && <div className="cal-tooltip__row cal-tooltip__flag cal-tooltip__flag--overdue">{oTip}</div>}
      {uTip && <div className="cal-tooltip__row cal-tooltip__flag cal-tooltip__flag--urgent">{uTip}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-views                                                          */
/* ------------------------------------------------------------------ */

interface SubViewProps {
  events: CalendarEvent[];
  anchor: dayjs.Dayjs;
  tooltip: TooltipState | null;
  onHover: (ev: CalendarEvent | null, e?: React.MouseEvent) => void;
  overdueMap?: OverdueMap;
  /** Called on single-click to highlight a day. */
  onDayClick?: (day: dayjs.Dayjs) => void;
  /** Called on double-click to drill into day view. */
  onDayDblClick?: (day: dayjs.Dayjs) => void;
  /** Currently selected (highlighted) day, if any. */
  selectedDay?: dayjs.Dayjs | null;
  /** Availability data per event id. */
  availabilityMap?: Map<string, EventAvailability>;
  /** Active availability display mode. */
  availMode?: 'off' | 'glow' | 'bar' | 'icon';
  /** Right-click context menu handler. */
  onEventContextMenu?: (ev: CalendarEvent, e: React.MouseEvent) => void;
}

interface WeekBar {
  event: LaneEvent;
  startCol: number;
  endCol: number;
  continues: boolean;
  started: boolean;
  showBar: boolean;
  whiskStartCol: number | null;
  whiskEndCol: number | null;
}

/* ---- Month View -------------------------------------------------- */

function MonthView({ events, anchor, tooltip, onHover, overdueMap, onDayClick, onDayDblClick, selectedDay, availabilityMap, availMode, onEventContextMenu }: SubViewProps) {
  const weeks = React.useMemo(
    () => getMonthWeeks(anchor.year(), anchor.month()),
    [anchor],
  );

  const today = dayjs().startOf('day');
  const currentMonth = anchor.month();

  // Filter events that fall within the visible date range
  const rangeStart = weeks[0][0];
  const rangeEnd = weeks[weeks.length - 1][6];

  const visibleEvents = React.useMemo(
    () =>
      events.filter((ev) => {
        // Use effective span (bar + whiskers) so whisker-only weeks are included
        const evStart = effectiveStart(ev);
        const evEnd = effectiveEnd(ev);
        const startsBeforeRangeEnds = evStart.isBefore(rangeEnd) || evStart.isSame(rangeEnd, 'day');
        const endsAfterRangeStarts = evEnd.isAfter(rangeStart) || evEnd.isSame(rangeStart, 'day');
        return startsBeforeRangeEnds && endsAfterRangeStarts;
      }),
    [events, rangeStart, rangeEnd],
  );

  const laneEvents = React.useMemo(
    () => assignLanesSimple(visibleEvents),
    [visibleEvents],
  );

  // For each week-row, collect bars that are visible in that week.
  // Whiskers may extend into weeks where the core bar isn't present,
  // so we also emit whisker-only entries for those weeks.
  const weekBars = React.useMemo(() => {
    return weeks.map((week) => {
      const weekStart = week[0];
      const weekEnd = week[6];
      const bars: {
        event: LaneEvent;
        startCol: number; // 0-6
        endCol: number; // 0-6
        continues: boolean; // bar continues into next week
        started: boolean; // bar started before this week
        /** Whether to render the main colored bar in this week row. */
        showBar: boolean;
        /** Left whisker column (prep/ship before event start), clamped to this week. null = no whisker. */
        whiskStartCol: number | null;
        /** Right whisker column (return after event end), clamped to this week. null = no whisker. */
        whiskEndCol: number | null;
      }[] = [];

      for (const ev of laneEvents) {
        // Effective span including whiskers
        const effectiveStart = ev.whiskStart && ev.whiskStart.isBefore(ev.start) ? ev.whiskStart : ev.start;
        const effectiveEnd = ev.whiskEnd && ev.whiskEnd.isAfter(ev.end) ? ev.whiskEnd : ev.end;
        // Skip entirely if nothing (bar or whisker) overlaps this week
        if (effectiveEnd.isBefore(weekStart) || effectiveStart.isAfter(weekEnd)) continue;

        const barOverlaps = !(ev.end.isBefore(weekStart) || ev.start.isAfter(weekEnd));

        const clampedStart = ev.start.isBefore(weekStart) ? weekStart : ev.start;
        const clampedEnd = ev.end.isAfter(weekEnd) ? weekEnd : ev.end;

        // If bar doesn't overlap this week, use clamped whisker edges for positioning
        const startCol = barOverlaps ? clampedStart.day() : 0;
        const endCol = barOverlaps ? clampedEnd.day() : 6;

        // Compute whisker columns within this week
        let whiskStartCol: number | null = null;
        if (ev.whiskStart && ev.whiskStart.isBefore(ev.start)) {
          const wsClamped = ev.whiskStart.isBefore(weekStart) ? weekStart : ev.whiskStart;
          if (!wsClamped.isAfter(weekEnd)) {
            whiskStartCol = wsClamped.day();
          }
        }
        let whiskEndCol: number | null = null;
        if (ev.whiskEnd && ev.whiskEnd.isAfter(ev.end)) {
          const weClamped = ev.whiskEnd.isAfter(weekEnd) ? weekEnd : ev.whiskEnd;
          if (!weClamped.isBefore(weekStart)) {
            whiskEndCol = weClamped.day();
          }
        }

        // For whisker-only weeks (no bar), determine which whisker(s) to show.
        // The left whisker spans from whiskStartCol to the bar start (or week end if bar is later).
        // The right whisker spans from bar end (or week start if bar is earlier) to whiskEndCol.
        if (!barOverlaps) {
          // This week is entirely before the bar → only left whisker possible
          const isBeforeBar = weekEnd.isBefore(ev.start);
          const isAfterBar = weekStart.isAfter(ev.end);
          if (isBeforeBar) {
            // Left whisker only: spans from whiskStartCol (clamped) to weekEnd
            if (whiskStartCol == null) continue; // no whisker in this week
            whiskEndCol = null; // no right whisker
          } else if (isAfterBar) {
            // Right whisker only: spans from weekStart to whiskEndCol (clamped)
            if (whiskEndCol == null) continue; // no whisker in this week
            whiskStartCol = null; // no left whisker
          }
        }

        bars.push({
          event: ev,
          startCol,
          endCol,
          continues: barOverlaps && ev.end.isAfter(weekEnd),
          started: barOverlaps && ev.start.isBefore(weekStart),
          showBar: barOverlaps,
          whiskStartCol,
          whiskEndCol,
        });
      }
      return bars;
    });
  }, [weeks, laneEvents]);

  // Compute max lanes per week for row height
  const maxLanesPerWeek = weekBars.map((bars) => {
    if (bars.length === 0) return 0;
    return Math.max(...bars.map((b) => b.event.lane)) + 1;
  });

  return (
    <div className="cal-month">
      <div className="cal-month__header">
        {DAY_LABELS.map((d) => (
          <div key={d} className="cal-month__day-label">
            {d}
          </div>
        ))}
      </div>
      <div className="cal-month__body">
        {weeks.map((week, wi) => {
          const minHeight = 60 + maxLanesPerWeek[wi] * 24;
          return (
            <div
              className="cal-month__week"
              key={week[0].format('YYYY-MM-DD')}
              style={{ minHeight }}
            >
              {/* Day cells (background) */}
              {week.map((day) => {
                const isToday = day.isSame(today, 'day');
                const isOtherMonth = day.month() !== currentMonth;
                const isSelected = selectedDay ? day.isSame(selectedDay, 'day') : false;
                return (
                  <div
                    key={day.format('YYYY-MM-DD')}
                    className={`cal-month__cell${isToday ? ' cal-month__cell--today' : ''}${isOtherMonth ? ' cal-month__cell--other' : ''}${isSelected ? ' cal-month__cell--selected' : ''}`}
                    onClick={() => onDayClick?.(day)}
                    onDoubleClick={() => onDayDblClick?.(day)}
                  >
                    <span className={`cal-month__date${isToday ? ' cal-month__date--today' : ''}`}>
                      {day.date()}
                    </span>
                  </div>
                );
              })}
              {/* Event bars + whiskers overlay */}
              {weekBars[wi].map((bar) => {
                const barTop = 26 + bar.event.lane * 24;
                const barColor = EVENT_COLORS[bar.event.colorIndex];

                // Whisker-only week (bar is on a different week row)
                if (!bar.showBar) {
                  // Left whisker: extends from whiskStartCol to end of week (col 6)
                  const hasLeft = bar.whiskStartCol != null;
                  // Right whisker: extends from start of week (col 0) to whiskEndCol
                  const hasRight = bar.whiskEndCol != null;
                  const wsCol = bar.whiskStartCol ?? 0;
                  const weCol = bar.whiskEndCol ?? 6;
                  const whiskLeft = (wsCol / 7) * 100;
                  const whiskWidth = ((weCol - wsCol + 1) / 7) * 100;
                  return (
                    <Link
                      key={`${bar.event.rowKey}-w${wi}-whisk`}
                      to={`/events/${bar.event.id}`}
                      className={`cal-whisk${hasLeft ? ' cal-whisk--left' : ''}${hasRight ? ' cal-whisk--right' : ''}`}
                      style={{
                        top: barTop,
                        left: `calc(${whiskLeft}% + 2px)`,
                        width: `calc(${whiskWidth}% - 4px)`,
                        height: 20,
                        '--whisk-color': barColor,
                      } as React.CSSProperties}
                      onMouseEnter={(e) => onHover(bar.event, e)}
                      onMouseMove={(e) => onHover(bar.event, e)}
                      onMouseLeave={() => onHover(null)}
                      onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                      aria-label={`Open event ${bar.event.code || bar.event.name}`}
                      title={`Open event ${bar.event.code || bar.event.name}`}
                    />
                  );
                }

                const span = bar.endCol - bar.startCol + 1;
                const leftPct = (bar.startCol / 7) * 100;
                const widthPct = (span / 7) * 100;
                const ac = availMode !== 'off' ? availColorClass(availabilityMap?.get(bar.event.id)) : '';
                const availCls = ac && availMode ? ` cal-bar--avail-${availMode}-${ac}` : '';
                const iconOffset = availMode === 'icon' && ac ? 14 : 0;

                // Whisker geometry (left = prep/ship, right = return)
                const hasLeftWhisk = bar.whiskStartCol != null && bar.whiskStartCol < bar.startCol;
                const hasRightWhisk = bar.whiskEndCol != null && bar.whiskEndCol > bar.endCol;

                return (
                  <React.Fragment key={`${bar.event.rowKey}-w${wi}`}>
                    {availMode === 'icon' && ac && !bar.started && (
                      <span
                        className={`cal-bar__avail-dot avail-icon avail-icon--${ac}`}
                        style={{ position: 'absolute', top: barTop + 5, left: `calc(${leftPct}% + 2px)`, zIndex: 2 }}
                      />
                    )}
                    {/* Left whisker: prep/ship dates before event start */}
                    {hasLeftWhisk && (
                      <Link
                        to={`/events/${bar.event.id}`}
                        className="cal-whisk cal-whisk--left"
                        style={{
                          top: barTop,
                          left: `calc(${(bar.whiskStartCol! / 7) * 100}% + 2px)`,
                          width: `calc(${((bar.startCol - bar.whiskStartCol!) / 7) * 100}%)`,
                          height: 20,
                          '--whisk-color': barColor,
                        } as React.CSSProperties}
                        onMouseEnter={(e) => onHover(bar.event, e)}
                        onMouseMove={(e) => onHover(bar.event, e)}
                        onMouseLeave={() => onHover(null)}
                        onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                        aria-label={`Open event ${bar.event.code || bar.event.name}`}
                        title={`Open event ${bar.event.code || bar.event.name}`}
                      />
                    )}
                    {/* Right whisker: return date after event end */}
                    {hasRightWhisk && (
                      <Link
                        to={`/events/${bar.event.id}`}
                        className="cal-whisk cal-whisk--right"
                        style={{
                          top: barTop,
                          left: `calc(${((bar.endCol + 1) / 7) * 100}% + 2px)`,
                          width: `calc(${((bar.whiskEndCol! - bar.endCol) / 7) * 100}% - 4px)`,
                          height: 20,
                          '--whisk-color': barColor,
                        } as React.CSSProperties}
                        onMouseEnter={(e) => onHover(bar.event, e)}
                        onMouseMove={(e) => onHover(bar.event, e)}
                        onMouseLeave={() => onHover(null)}
                        onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                        aria-label={`Open event ${bar.event.code || bar.event.name}`}
                        title={`Open event ${bar.event.code || bar.event.name}`}
                      />
                    )}
                    <Link
                      to={`/events/${bar.event.id}`}
                      className={`cal-bar${bar.continues ? ' cal-bar--continues' : ''}${bar.started ? ' cal-bar--started' : ''}${availCls}`}
                      style={{
                        top: barTop,
                        left: `calc(${leftPct}% + ${2 + iconOffset}px)`,
                        width: `calc(${widthPct}% - ${4 + iconOffset}px)`,
                        backgroundColor: barColor,
                      }}
                      onMouseEnter={(e) => onHover(bar.event, e)}
                      onMouseMove={(e) => onHover(bar.event, e)}
                      onMouseLeave={() => onHover(null)}
                      onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                      title=""
                    >
                      <span className="cal-bar__label">
                        <StageIcon status={bar.event.status} />
                        <EventFlags row={overdueMap?.get(bar.event.id) ?? null} compact />
                        {!bar.started && <strong>{bar.event.code}</strong>}
                        {!bar.started && bar.event.code && ' '}
                        {bar.event.name}
                      </span>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>
      {tooltip && <EventTooltip {...tooltip} overdueMap={overdueMap} availabilityMap={availMode === 'off' ? undefined : availabilityMap} />}
    </div>
  );
}

/* ---- Week View --------------------------------------------------- */

function WeekView({ events, anchor, tooltip, onHover, overdueMap, onDayClick, onDayDblClick, selectedDay, availabilityMap, availMode, onEventContextMenu }: SubViewProps) {
  const days = React.useMemo(() => getWeekDays(anchor), [anchor]);
  const today = dayjs().startOf('day');

  const rangeStart = days[0];
  const rangeEnd = days[6];

  const visibleEvents = React.useMemo(
    () =>
      events.filter((ev) => {
        // Use effective span (bar + whiskers) so whisker events are included
        const evEnd = effectiveEnd(ev);
        const evStart = effectiveStart(ev);
        return (
          (evEnd.isSame(rangeStart, 'day') || evEnd.isAfter(rangeStart)) &&
          (evStart.isSame(rangeEnd, 'day') || evStart.isBefore(rangeEnd))
        );
      }),
    [events, rangeStart, rangeEnd],
  );

  const laneEvents = React.useMemo(
    () => assignLanesSimple(visibleEvents),
    [visibleEvents],
  );

  const bars = React.useMemo((): WeekBar[] => {
    const weekBars: WeekBar[] = [];
    for (const ev of laneEvents) {
      const barOverlaps = !(ev.end.isBefore(rangeStart) || ev.start.isAfter(rangeEnd));
      const clampedStart = barOverlaps ? (ev.start.isBefore(rangeStart) ? rangeStart : ev.start) : rangeStart;
      const clampedEnd = barOverlaps ? (ev.end.isAfter(rangeEnd) ? rangeEnd : ev.end) : rangeEnd;
      const startCol = clampedStart.day();
      const endCol = clampedEnd.day();

      let whiskStartCol: number | null = null;
      if (ev.whiskStart && ev.whiskStart.isBefore(ev.start)) {
        const wsClamped = ev.whiskStart.isBefore(rangeStart) ? rangeStart : ev.whiskStart;
        if (!wsClamped.isAfter(rangeEnd)) {
          whiskStartCol = wsClamped.day();
        }
      }
      let whiskEndCol: number | null = null;
      if (ev.whiskEnd && ev.whiskEnd.isAfter(ev.end)) {
        const weClamped = ev.whiskEnd.isAfter(rangeEnd) ? rangeEnd : ev.whiskEnd;
        if (!weClamped.isBefore(rangeStart)) {
          whiskEndCol = weClamped.day();
        }
      }

      if (!barOverlaps) {
        const isBeforeBar = rangeEnd.isBefore(ev.start);
        const isAfterBar = rangeStart.isAfter(ev.end);
        if (isBeforeBar) {
          if (whiskStartCol == null) continue;
          whiskEndCol = null;
        } else if (isAfterBar) {
          if (whiskEndCol == null) continue;
          whiskStartCol = null;
        }
      }

      weekBars.push({
        event: ev,
        startCol,
        endCol,
        continues: ev.end.isAfter(rangeEnd),
        started: ev.start.isBefore(rangeStart),
        showBar: barOverlaps,
        whiskStartCol,
        whiskEndCol,
      });
    }
    return weekBars;
  }, [laneEvents, rangeStart, rangeEnd]);

  const maxLane = bars.length > 0 ? Math.max(...bars.map((b) => b.event.lane)) + 1 : 0;
  const bodyHeight = Math.max(200, 50 + maxLane * 32);

  return (
    <div className="cal-week">
      <div className="cal-week__header">
        {days.map((day) => {
          const isToday = day.isSame(today, 'day');
          const isSelected = selectedDay ? day.isSame(selectedDay, 'day') : false;
          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={`cal-week__day-label cal-week__day-label--click${isToday ? ' cal-week__day-label--today' : ''}${isSelected ? ' cal-week__day-label--selected' : ''}`}
              onClick={() => onDayClick?.(day)}
              onDoubleClick={() => onDayDblClick?.(day)}
              role="button"
              tabIndex={0}
            >
              <span className="cal-week__day-name">{DAY_LABELS[day.day()]}</span>
              <span className={`cal-week__day-num${isToday ? ' cal-week__day-num--today' : ''}`}>
                {day.date()}
              </span>
            </div>
          );
        })}
      </div>
      <div className="cal-week__body" style={{ minHeight: bodyHeight }}>
        {/* Column lines */}
        {days.map((day) => {
          const isToday = day.isSame(today, 'day');
          const isSelected = selectedDay ? day.isSame(selectedDay, 'day') : false;
          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={`cal-week__col${isToday ? ' cal-week__col--today' : ''}${isSelected ? ' cal-week__col--selected' : ''}`}
            />
          );
        })}
        {/* Event bars */}
        {bars.map((bar) => {
          const ac = availMode !== 'off' ? availColorClass(availabilityMap?.get(bar.event.id)) : '';
          const barTop = 8 + bar.event.lane * 32;
          const barColor = EVENT_COLORS[bar.event.colorIndex];

          if (!bar.showBar) {
            const hasLeft = bar.whiskStartCol != null;
            const whiskStartCol = hasLeft ? bar.whiskStartCol! : 0;
            const whiskEndCol = hasLeft ? 6 : bar.whiskEndCol!;
            const whiskSpan = whiskEndCol - whiskStartCol + 1;
            const whiskLeftPct = (whiskStartCol / 7) * 100;
            const whiskWidthPct = (whiskSpan / 7) * 100;
            return (
              <Link
                key={`${bar.event.rowKey}-week-whisk-${hasLeft ? 'left' : 'right'}`}
                to={`/events/${bar.event.id}`}
                className={`cal-whisk ${hasLeft ? 'cal-whisk--left' : 'cal-whisk--right'}`}
                style={{
                  top: barTop,
                  left: `calc(${whiskLeftPct}% + 3px)`,
                  width: `calc(${whiskWidthPct}% - 6px)`,
                  height: 26,
                  '--whisk-color': barColor,
                } as React.CSSProperties}
                onMouseEnter={(e) => onHover(bar.event, e)}
                onMouseMove={(e) => onHover(bar.event, e)}
                onMouseLeave={() => onHover(null)}
                onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                aria-label={`Open event ${bar.event.code || bar.event.name}`}
                title={`Open event ${bar.event.code || bar.event.name}`}
              />
            );
          }

          const span = bar.endCol - bar.startCol + 1;
          const leftPct = (bar.startCol / 7) * 100;
          const widthPct = (span / 7) * 100;
          const availCls = ac && availMode ? ` cal-bar--avail-${availMode}-${ac}` : '';
          const iconOffset = availMode === 'icon' && ac ? 14 : 0;

          const hasLeftWhisk = bar.whiskStartCol != null && bar.whiskStartCol < bar.startCol;
          const hasRightWhisk = bar.whiskEndCol != null && bar.whiskEndCol > bar.endCol;

          return (
            <React.Fragment key={bar.event.rowKey}>
              {availMode === 'icon' && ac && !bar.started && (
                <span
                  className={`cal-bar__avail-dot avail-icon avail-icon--${ac}`}
                  style={{ position: 'absolute', top: barTop + 8, left: `calc(${leftPct}% + 3px)`, zIndex: 2 }}
                />
              )}
              {/* Left whisker: prep/ship dates before event start */}
              {hasLeftWhisk && (
                <Link
                  to={`/events/${bar.event.id}`}
                  className="cal-whisk cal-whisk--left"
                  style={{
                    top: barTop,
                    left: `calc(${(bar.whiskStartCol! / 7) * 100}% + 3px)`,
                    width: `calc(${((bar.startCol - bar.whiskStartCol!) / 7) * 100}%)`,
                    height: 26,
                    '--whisk-color': barColor,
                  } as React.CSSProperties}
                  onMouseEnter={(e) => onHover(bar.event, e)}
                  onMouseMove={(e) => onHover(bar.event, e)}
                  onMouseLeave={() => onHover(null)}
                  onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                  aria-label={`Open event ${bar.event.code || bar.event.name}`}
                  title={`Open event ${bar.event.code || bar.event.name}`}
                />
              )}
              {/* Right whisker: return date after event end */}
              {hasRightWhisk && (
                <Link
                  to={`/events/${bar.event.id}`}
                  className="cal-whisk cal-whisk--right"
                  style={{
                    top: barTop,
                    left: `calc(${((bar.endCol + 1) / 7) * 100}% + 3px)`,
                    width: `calc(${((bar.whiskEndCol! - bar.endCol) / 7) * 100}% - 6px)`,
                    height: 26,
                    '--whisk-color': barColor,
                  } as React.CSSProperties}
                  onMouseEnter={(e) => onHover(bar.event, e)}
                  onMouseMove={(e) => onHover(bar.event, e)}
                  onMouseLeave={() => onHover(null)}
                  onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                  aria-label={`Open event ${bar.event.code || bar.event.name}`}
                  title={`Open event ${bar.event.code || bar.event.name}`}
                />
              )}
              <Link
                to={`/events/${bar.event.id}`}
                className={`cal-bar cal-bar--week${bar.continues ? ' cal-bar--continues' : ''}${bar.started ? ' cal-bar--started' : ''}${availCls}`}
                style={{
                  top: barTop,
                  left: `calc(${leftPct}% + ${3 + iconOffset}px)`,
                  width: `calc(${widthPct}% - ${6 + iconOffset}px)`,
                  backgroundColor: barColor,
                }}
                onMouseEnter={(e) => onHover(bar.event, e)}
                onMouseMove={(e) => onHover(bar.event, e)}
                onMouseLeave={() => onHover(null)}
                onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(bar.event, e) : undefined}
                title=""
              >
                <span className="cal-bar__label">
                  <StageIcon status={bar.event.status} />
                  <EventFlags row={overdueMap?.get(bar.event.id) ?? null} compact />
                  <strong>{bar.event.code}</strong> {bar.event.name}
                  {bar.event.venueName && (
                    <span className="cal-bar__venue"> - {bar.event.venueName}</span>
                  )}
                </span>
              </Link>
            </React.Fragment>
          );
        })}
      </div>
      {tooltip && <EventTooltip {...tooltip} overdueMap={overdueMap} availabilityMap={availMode === 'off' ? undefined : availabilityMap} />}
    </div>
  );
}

/* ---- Day View ---------------------------------------------------- */

function DayView({ events, anchor, tooltip, onHover, overdueMap, availabilityMap, availMode, onEventContextMenu }: SubViewProps) {
  const today = dayjs().startOf('day');
  const isToday = anchor.isSame(today, 'day');

  // Include events whose full span (bar + whiskers) touches this day
  const dayEvents = React.useMemo(
    () =>
      events
        .filter((ev) => {
          const evEffStart = effectiveStart(ev);
          const evEffEnd = effectiveEnd(ev);
          return (
            (evEffStart.isSame(anchor, 'day') || evEffStart.isBefore(anchor)) &&
            (evEffEnd.isSame(anchor, 'day') || evEffEnd.isAfter(anchor))
          );
        })
        .sort((a, b) => effectiveStart(a).diff(effectiveStart(b)) || effectiveEnd(b).diff(effectiveStart(b)) - effectiveEnd(a).diff(effectiveStart(a))),
    [events, anchor],
  );

  const laneEvents = React.useMemo(
    () => assignLanesSimple(dayEvents),
    [dayEvents],
  );

  return (
    <div className="cal-day">
      <div className={`cal-day__header${isToday ? ' cal-day__header--today' : ''}`}>
        <span className="cal-day__weekday">{DAY_LABELS[anchor.day()]}</span>
        <span className={`cal-day__num${isToday ? ' cal-day__num--today' : ''}`}>
          {anchor.date()}
        </span>
        <span className="cal-day__month">{anchor.format('MMMM YYYY')}</span>
      </div>
      <div className="cal-day__body">
        {laneEvents.length === 0 && (
          <div className="cal-day__empty">No events on this day</div>
        )}
        {laneEvents.map((ev) => {
          const startStr = ev.start.format('MMM D');
          const endStr = ev.end.format('MMM D');
          const dateRange = ev.start.isSame(ev.end, 'day')
            ? startStr
            : `${startStr} - ${endStr}`;
          const ac = availMode !== 'off' ? availColorClass(availabilityMap?.get(ev.id)) : '';
          const dayAvailCls = ac && availMode ? ` cal-day__event--avail-${availMode}-${ac}` : '';
          const hasWarehouse = ev.whiskStart || ev.whiskEnd;
          // Is this day outside the core event window (whisker-only)?
          const isWhiskOnly = anchor.isBefore(ev.start) || anchor.isAfter(ev.end);
          return (
            <Link
              key={ev.rowKey}
              to={`/events/${ev.id}`}
              className={`cal-day__event${dayAvailCls}${isWhiskOnly ? ' cal-day__event--whisk-only' : ''}`}
              style={{ borderLeftColor: EVENT_COLORS[ev.colorIndex] }}
              onMouseEnter={(e) => onHover(ev, e)}
              onMouseMove={(e) => onHover(ev, e)}
              onMouseLeave={() => onHover(null)}
              onContextMenu={onEventContextMenu ? (e) => onEventContextMenu(ev, e) : undefined}
            >
              <div className="cal-day__event-top">
                {availMode === 'icon' && ac && <span className={`avail-icon avail-icon--${ac}`} />}
                <StageIcon status={ev.status} />
                <EventFlags row={overdueMap?.get(ev.id) ?? null} compact />
                <strong className="cal-day__event-code">{ev.code}</strong>
                <span className="cal-day__event-name">{ev.name}</span>
                {ev.status && (
                  <span className="pill subtle cal-day__event-status">
                    {formatStatusLabel(ev.status)}
                  </span>
                )}
                {isWhiskOnly && (
                  <span className="pill subtle cal-day__event-whisk-label">
                    {anchor.isBefore(ev.start) ? 'Prep' : 'Return'}
                  </span>
                )}
              </div>
              <div className="cal-day__event-meta text-xs fg-muted">
                {dateRange}
                {ev.venueName && ` \u00B7 ${ev.venueName}`}
                {ev.customerName && ` \u00B7 ${ev.customerName}`}
              </div>
              {hasWarehouse && (() => {
                // Build a mini candlestick timeline bar
                const effStart = effectiveStart(ev);
                const effEnd = effectiveEnd(ev);
                const totalDays = effEnd.diff(effStart, 'day') || 1;
                const barLeftPct = ((ev.start.diff(effStart, 'day')) / totalDays) * 100;
                const barWidthPct = ((ev.end.diff(ev.start, 'day') || 1) / totalDays) * 100;
                const leftWhiskPct = ev.whiskStart ? ((ev.start.diff(effStart, 'day')) / totalDays) * 100 : 0;
                const rightWhiskPct = ev.whiskEnd ? ((effEnd.diff(ev.end, 'day')) / totalDays) * 100 : 0;
                const barColor = EVENT_COLORS[ev.colorIndex];
                return (
                  <div className="cal-day__candlestick">
                    <div className="cal-day__candlestick-track">
                      {/* Left whisker */}
                      {ev.whiskStart && leftWhiskPct > 0 && (
                        <span
                          className="cal-day__candlestick-whisk cal-day__candlestick-whisk--left"
                          style={{
                            left: '0%',
                            width: `${barLeftPct}%`,
                            '--whisk-color': barColor,
                          } as React.CSSProperties}
                        />
                      )}
                      {/* Main bar */}
                      <span
                        className="cal-day__candlestick-bar"
                        style={{
                          left: `${barLeftPct}%`,
                          width: `${barWidthPct}%`,
                          backgroundColor: barColor,
                        }}
                      />
                      {/* Right whisker */}
                      {ev.whiskEnd && rightWhiskPct > 0 && (
                        <span
                          className="cal-day__candlestick-whisk cal-day__candlestick-whisk--right"
                          style={{
                            left: `${barLeftPct + barWidthPct}%`,
                            width: `${rightWhiskPct}%`,
                            '--whisk-color': barColor,
                          } as React.CSSProperties}
                        />
                      )}
                    </div>
                    <div className="cal-day__candlestick-labels text-xs fg-muted">
                      {ev.whiskStart && <span>Prep: {ev.whiskStart.format('MMM D')}</span>}
                      <span style={{ marginLeft: 'auto' }}>{ev.whiskEnd && `Return: ${ev.whiskEnd.format('MMM D')}`}</span>
                    </div>
                  </div>
                );
              })()}
              <div className="cal-day__event-meta text-xs fg-muted">
                PM: {ev.pmName ?? '--'} | SP: {ev.spName ?? '--'}
                {' \u00B7 '}
                {ev.periodLabel ? 'Event total: ' : ''}{formatCurrency(ev.customerTotalAmount)}
                {ev.itemCount > 0
                  ? ` \u00B7 ${ev.itemCount} item${ev.itemCount !== 1 ? 's' : ''}`
                  : ''}
              </div>
            </Link>
          );
        })}
      </div>
      {tooltip && <EventTooltip {...tooltip} overdueMap={overdueMap} availabilityMap={availMode === 'off' ? undefined : availabilityMap} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Context Menu                                                       */
/* ------------------------------------------------------------------ */

interface ContextMenuState {
  event: CalendarEvent;
  x: number;
  y: number;
}

function CalendarContextMenu({
  ctx,
  onClose,
  onDelete,
  onStageUpdated,
  onBeginPicking,
}: {
  ctx: ContextMenuState;
  onClose: () => void;
  onDelete?: (eventId: string, name: string | null) => void;
  onStageUpdated?: (payload?: CalendarStageUpdatePayload) => void;
  onBeginPicking?: (eventId: string) => void;
}) {
  const navigate = useNavigate();
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [stageBusy, setStageBusy] = React.useState(false);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);

  const { lookup: orderStageLookup } = useOrderStageReference();

  // Derive ordered statuses and current/next status for this event
  const orderedStatuses = React.useMemo(
    () =>
      Object.entries(orderStageLookup)
        .sort((a, b) => a[1].stageIndex - b[1].stageIndex)
        .map(([status]) => ({
          status,
          label: orderStageLookup[status]?.label ?? formatOrderStatusLabel(status),
        })),
    [orderStageLookup],
  );

  const statuses = ctx.event.status ? [ctx.event.status.trim()] : [];
  const stageInfo = deriveOrderStage(statuses, orderStageLookup);
  const currentStatus = stageInfo?.status ?? null;
  const nextOrderStatus = React.useMemo(() => {
    const idx = orderedStatuses.findIndex((s) => s.status === (currentStatus ?? ''));
    return idx >= 0 && idx < orderedStatuses.length - 1 ? orderedStatuses[idx + 1]?.status ?? null : null;
  }, [orderedStatuses, currentStatus]);
  const nextLabel = nextOrderStatus
    ? orderedStatuses.find((s) => s.status === nextOrderStatus)?.label ?? formatOrderStatusLabel(nextOrderStatus)
    : null;

  // Smart positioning with viewport clamping
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  React.useLayoutEffect(() => {
    const menuWidth = 220;
    const menuHeight = 260;
    let top = ctx.y;
    let left = ctx.x;
    if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
    if (left < 8) left = 8;
    if (top + menuHeight > window.innerHeight - 8) top = ctx.y - menuHeight;
    if (top < 8) top = 8;
    setPos({ top, left });
  }, [ctx.x, ctx.y]);

  // Close on click-outside or Escape
  React.useEffect(() => {
    if (confirmationOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [confirmationOpen, onClose]);

  const eventId = ctx.event.id;
  const linkedFulfillmentOrderCountsQuery = useQuery({
    queryKey: ['event-linked-fulfillment-orders', eventId],
    enabled: Boolean(eventId),
    queryFn: async () => {
      const { data, error } = await fetchLinkedFulfillmentOrderCounts(eventId);
      if (error) throw error;
      return data;
    },
  });
  const linkedTransferOrderCount = linkedFulfillmentOrderCountsQuery.data?.transferOrderCount ?? 0;
  const linkedPurchaseOrderCount = linkedFulfillmentOrderCountsQuery.data?.purchaseOrderCount ?? 0;
  const hasLinkedFulfillmentOrders = linkedTransferOrderCount > 0 || linkedPurchaseOrderCount > 0;

  const handleOpen = () => {
    onClose();
    navigate(`/events/${eventId}`);
  };

  const handleMoveToNextStage = async () => {
    if (!eventId || !nextOrderStatus) return;
    if (nextOrderStatus === 'pending_acceptance') {
      onClose();
      navigate(`/events/${eventId}/overview?sendToClient=1`);
      return;
    }
    if (nextOrderStatus === 'confirmed') {
      setConfirmationOpen(true);
      return;
    }
    setStageBusy(true);
    try {
      const { data, error } = await updateEventStageStatus({ eventId, status: nextOrderStatus });
      if (error) throw error;
      if (data?.invoiceReview) {
        window.alert(invoiceReviewHoldMessage(data.invoiceReview));
        onClose();
        return;
      }
      onStageUpdated?.({ eventId, status: nextOrderStatus });
      onClose();
    } catch (err) {
      console.error('Failed to update event stage:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to move event to next stage.');
    } finally {
      setStageBusy(false);
    }
  };

  const handleConvertToReservation = () => {
    onClose();
    navigate(`/events/${eventId}?tab=items`, { state: { convertToReservation: true } });
  };

  const handleCancelLoseOrder = async () => {
    if (!eventId) return;
    if (!window.confirm('Cancel/Lose this event? The event stage will be set to Cancelled.')) return;
    const reason = window.prompt('Please provide a reason for cancelling or losing this event (required):');
    if (reason === null) return;
    if (!(reason != null && String(reason).trim().length > 0)) {
      window.alert('A reason is required.');
      return;
    }
    setCancelBusy(true);
    try {
      const { error } = await updateEventStageStatus({ eventId, status: 'cancelled', reason: String(reason).trim() });
      if (error) throw error;
      onClose();
      onStageUpdated?.({ eventId, status: 'cancelled' });
    } catch (err) {
      console.error('Failed to cancel event:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to cancel event.');
    } finally {
      setCancelBusy(false);
    }
  };

  const handleDuplicate = () => {
    onClose();
    navigate(`/events?create=1&duplicateFrom=${eventId}`);
  };

  const handleDelete = () => {
    onClose();
    onDelete?.(eventId, ctx.event.name);
  };

  if (!pos) return null;

  const fulfillmentScanMode = defaultEventFulfillmentScanMode(ctx.event.status);
  const isPickingStage = canOpenEventFulfillmentScanner(ctx.event.status);

  const menu = (
    <div
      ref={menuRef}
      className="dropdown-menu"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        zIndex: 10000,
        minWidth: '220px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dropdown-menu__section">
        {hasLinkedFulfillmentOrders ? (
          <div
            className="dropdown-menu__header"
            role="status"
            aria-live="polite"
            style={{
              color: 'var(--warning-700, var(--warning))',
              borderBottom: '1px solid color-mix(in srgb, var(--warning) 35%, var(--border))',
            }}
          >
            {`${linkedTransferOrderCount} TO${linkedTransferOrderCount === 1 ? '' : 's'} · ${linkedPurchaseOrderCount} PO${linkedPurchaseOrderCount === 1 ? '' : 's'} linked`}
            <br />
            {'Not auto-cancelled on stage change'}
          </div>
        ) : null}
        <button type="button" className="dropdown-menu__item" onClick={handleOpen}>
          Open Event
        </button>
      </div>
      <div className="dropdown-menu__section" style={{ borderTop: '1px solid var(--border-light, rgba(0,0,0,0.06))' }}>
        {nextOrderStatus && (
          <button
            type="button"
            className="dropdown-menu__item"
            onClick={handleMoveToNextStage}
            disabled={stageBusy}
          >
            {stageBusy ? 'Updating\u2026' : `Move to next stage${nextLabel ? ` (${nextLabel})` : ''}`}
          </button>
        )}
        {isPickingStage && onBeginPicking ? (
          <button
            type="button"
            className="dropdown-menu__item"
            onClick={() => { onClose(); onBeginPicking(eventId); }}
          >
            {fulfillmentScanMode === 'returning'
              ? 'Begin Returning'
              : 'Begin Picking'}
          </button>
        ) : (
          <button type="button" className="dropdown-menu__item" onClick={handleConvertToReservation}>
            Convert to reservation
          </button>
        )}
        <button
          type="button"
          className="dropdown-menu__item"
          onClick={handleCancelLoseOrder}
          disabled={cancelBusy}
        >
          {cancelBusy ? 'Updating\u2026' : 'Cancel/Lose order'}
        </button>
        <button type="button" className="dropdown-menu__item" onClick={handleDuplicate}>
          Duplicate
        </button>
        <button
          type="button"
          className="dropdown-menu__item"
          onClick={handleDelete}
          style={{ color: 'var(--danger)' }}
        >
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(menu, document.body)}
      <EventConfirmationModal
        eventId={eventId}
        eventStatus={currentStatus}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onCompleted={(result) => {
          if (result.confirmation.pendingRequest) return;
          onStageUpdated?.({
            eventId,
            status: result.status || result.confirmation.eventStatus || 'confirmed',
            reservationJobStatus: result.reservationJobStatus,
          });
          setConfirmationOpen(false);
          onClose();
        }}
        onSendToClient={() => {
          setConfirmationOpen(false);
          onClose();
          navigate(`/events/${eventId}/overview?sendToClient=1`);
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function EventsCalendarView({ events, showPeriodSummaries, overdueMap, onDateRangeChange, availabilityMap, availabilityMode, isActionable, onDelete, onStageUpdated, onBeginPicking, stageLegendItems, showWarehouseWhiskers = true, onShowWarehouseWhiskersChange, showHiddenEvents = false, onShowHiddenEventsChange, hiddenEventCount = 0, canRevealHiddenEvents = false }: EventsCalendarViewProps) {
  const safeHiddenEventCount = Number.isFinite(hiddenEventCount)
    ? Math.max(0, Math.trunc(hiddenEventCount))
    : 0;
  const [mode, setMode] = React.useState<CalendarMode>('month');
  const [anchor, setAnchor] = React.useState(() => dayjs().startOf('day'));
  const [tooltip, setTooltip] = React.useState<TooltipState | null>(null);
  const [contextMenu, setContextMenu] = React.useState<ContextMenuState | null>(null);
  const [showPicker, setShowPicker] = React.useState(false);
  const [pickerYear, setPickerYear] = React.useState(() => dayjs().year());
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const [internalShowWarehouseWhiskers, setInternalShowWarehouseWhiskers] = React.useState(showWarehouseWhiskers);
  const warehouseWhiskerToggleControlled = typeof onShowWarehouseWhiskersChange === 'function';
  const effectiveShowWarehouseWhiskers = warehouseWhiskerToggleControlled
    ? showWarehouseWhiskers
    : internalShowWarehouseWhiskers;

  // Phones get the two-row toolbar (its toggles in the ⋯ menu) and a folded stage legend.
  const isPhone = useLibraryPhoneLayout();

  const calEvents = React.useMemo(() => toCalendarEvents(events, effectiveShowWarehouseWhiskers, showHiddenEvents, showPeriodSummaries), [events, effectiveShowWarehouseWhiskers, showHiddenEvents, showPeriodSummaries]);
  const stageLegend = React.useMemo(() => buildStageLegend(stageLegendItems), [stageLegendItems]);

  React.useEffect(() => {
    if (!warehouseWhiskerToggleControlled) setInternalShowWarehouseWhiskers(showWarehouseWhiskers);
  }, [showWarehouseWhiskers, warehouseWhiskerToggleControlled]);

  const handleShowWarehouseWhiskersChange = React.useCallback((checked: boolean) => {
    if (!warehouseWhiskerToggleControlled) {
      setInternalShowWarehouseWhiskers(checked);
    }
    onShowWarehouseWhiskersChange?.(checked);
  }, [onShowWarehouseWhiskersChange, warehouseWhiskerToggleControlled]);

  const handleShowHiddenEventsChange = React.useCallback((checked: boolean) => {
    onShowHiddenEventsChange?.(checked);
  }, [onShowHiddenEventsChange]);

  // Sync picker year when anchor changes externally (prev/next nav)
  React.useEffect(() => {
    setPickerYear(anchor.year());
  }, [anchor]);

  // Close picker on Escape or click outside
  React.useEffect(() => {
    if (!showPicker) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowPicker(false);
    }
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  // Report visible date window to parent whenever anchor or mode changes
  React.useEffect(() => {
    if (!onDateRangeChange) return;
    let windowStart: dayjs.Dayjs;
    let windowEnd: dayjs.Dayjs;
    if (mode === 'month') {
      const weeks = getMonthWeeks(anchor.year(), anchor.month());
      windowStart = weeks[0][0];
      windowEnd = weeks[weeks.length - 1][6];
    } else if (mode === 'week') {
      windowStart = anchor.startOf('week');
      windowEnd = windowStart.add(6, 'day');
    } else {
      windowStart = anchor;
      windowEnd = anchor;
    }
    onDateRangeChange(windowStart.format('YYYY-MM-DD'), windowEnd.format('YYYY-MM-DD'));
  }, [anchor, mode, onDateRangeChange]);

  const handleHover = React.useCallback(
    (ev: CalendarEvent | null, e?: React.MouseEvent) => {
      if (!ev || !e) {
        setTooltip(null);
        return;
      }
      setTooltip({ event: ev, x: e.clientX, y: e.clientY });
    },
    [],
  );

  /** Right-click context menu handler for event bars/cards. */
  const handleEventContextMenu = React.useCallback(
    (ev: CalendarEvent, e: React.MouseEvent) => {
      if (!isActionable) return;
      e.preventDefault();
      e.stopPropagation();
      setTooltip(null); // hide tooltip when context menu opens
      setContextMenu({ event: ev, x: e.clientX, y: e.clientY });
    },
    [isActionable],
  );

  const closeContextMenu = React.useCallback(() => setContextMenu(null), []);

  const [selectedDay, setSelectedDay] = React.useState<dayjs.Dayjs | null>(null);

  /** Single-click: highlight the day (toggle off if already selected). */
  const handleDayClick = React.useCallback((day: dayjs.Dayjs) => {
    setSelectedDay((prev) => (prev && prev.isSame(day, 'day') ? null : day.startOf('day')));
  }, []);

  /** Double-click: drill into day view for that date. */
  const handleDayDblClick = React.useCallback((day: dayjs.Dayjs) => {
    setSelectedDay(null);
    setAnchor(day.startOf('day'));
    setMode('day');
  }, []);

  /* Navigation */
  function goToday() {
    setAnchor(dayjs().startOf('day'));
  }

  function goPrev() {
    if (mode === 'month') setAnchor((a) => a.subtract(1, 'month'));
    else if (mode === 'week') setAnchor((a) => a.subtract(1, 'week'));
    else setAnchor((a) => a.subtract(1, 'day'));
  }

  function goNext() {
    if (mode === 'month') setAnchor((a) => a.add(1, 'month'));
    else if (mode === 'week') setAnchor((a) => a.add(1, 'week'));
    else setAnchor((a) => a.add(1, 'day'));
  }

  /* Title */
  let title = '';
  if (mode === 'month') {
    title = anchor.format('MMMM YYYY');
  } else if (mode === 'week') {
    const weekStart = anchor.startOf('week');
    const weekEnd = weekStart.add(6, 'day');
    title =
      weekStart.month() === weekEnd.month()
        ? `${weekStart.format('MMM D')} - ${weekEnd.format('D, YYYY')}`
        : `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
  } else {
    title = anchor.format(isPhone ? 'ddd, MMM D, YYYY' : 'dddd, MMMM D, YYYY');
  }

  const todayButton = (
    <button type="button" className="btn xs btn-outline cal-toolbar__today" onClick={goToday}>
      Today
    </button>
  );

  const optionItems: MenuButtonItem[] = [
    ...(canRevealHiddenEvents ? [{
      key: 'hidden',
      label: 'Show hidden events',
      description: showHiddenEvents ? undefined : `${safeHiddenEventCount} ${safeHiddenEventCount === 1 ? 'event' : 'events'} hidden`,
      checked: showHiddenEvents,
      onSelect: () => handleShowHiddenEventsChange(!showHiddenEvents),
    }] : []),
    {
      key: 'whiskers',
      label: 'Show warehouse whiskers',
      description: 'Prep, ship and return dates around each event',
      checked: effectiveShowWarehouseWhiskers,
      onSelect: () => handleShowWarehouseWhiskersChange(!effectiveShowWarehouseWhiskers),
    },
  ];

  const legendItems = (
    <div className="cal-legend__items">
      {stageLegend.map(({ key, label }) => (
        <span key={key} className="cal-legend__item">
          <span className="cal-legend__icon">{STAGE_ICONS[key] ?? STAGE_ICON_FALLBACK}</span>
          <span className="cal-legend__label">{label}</span>
        </span>
      ))}
    </div>
  );

  return (
    <div className="cal-root">
      {/* Toolbar */}
      <div className={`cal-toolbar${isPhone ? ' cal-toolbar--phone' : ''}`}>
        <div className="cal-toolbar__nav">
          <button type="button" className="btn xs btn-outline cal-toolbar__arrow" onClick={goPrev} aria-label="Previous">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button type="button" className="btn xs btn-outline cal-toolbar__arrow" onClick={goNext} aria-label="Next">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          {isPhone ? null : todayButton}
          <div className="cal-toolbar__title-wrap" ref={pickerRef}>
            <button
              type="button"
              className="cal-toolbar__title cal-toolbar__title--clickable"
              onClick={() => setShowPicker((v) => !v)}
              aria-expanded={showPicker}
              aria-haspopup="true"
            >
              {title}
              <svg className="cal-toolbar__title-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {showPicker && (
              <div className="cal-picker dropdown-menu">
                <div className="cal-picker__year-row">
                  <button type="button" className="cal-picker__year-arrow" onClick={() => setPickerYear((y) => y - 1)} aria-label="Previous year">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span className="cal-picker__year-label">{pickerYear}</span>
                  <button type="button" className="cal-picker__year-arrow" onClick={() => setPickerYear((y) => y + 1)} aria-label="Next year">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
                <div className="cal-picker__month-grid">
                  {MONTH_ABBR.map((label, idx) => {
                    const isCurrent = anchor.year() === pickerYear && anchor.month() === idx;
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`cal-picker__month${isCurrent ? ' cal-picker__month--active' : ''}`}
                        onClick={() => {
                          setAnchor(dayjs().year(pickerYear).month(idx).date(1).startOf('day'));
                          setShowPicker(false);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {isPhone ? todayButton : (
            <span className="text-xs fg-muted events-calendar__count" style={{ marginLeft: '0.25rem' }}>
              {calEvents.length === 0
                ? `(${events.length} events without dates)`
                : `(${new Set(calEvents.map(event => event.id)).size} events)`}
            </span>
          )}
        </div>
        <div className="cal-toolbar__modes">
          {!isPhone && canRevealHiddenEvents && (
            <label className="cal-toolbar__toggle text-xs" title="Temporarily reveal events hidden from this calendar view">
              <input
                type="checkbox"
                aria-label="Show hidden events"
                checked={showHiddenEvents}
                onChange={(event) => handleShowHiddenEventsChange(event.currentTarget.checked)}
              />
              <span>Show hidden events</span>
              {!showHiddenEvents && (
                <span className="cal-toolbar__count" aria-label={`${safeHiddenEventCount} hidden events`}>
                  {safeHiddenEventCount}
                </span>
              )}
            </label>
          )}
          {!isPhone && (
            <label className="cal-toolbar__toggle text-xs" title="Show warehouse prep, ship, and return whiskers">
              <input
                type="checkbox"
                aria-label="Show warehouse whiskers"
                checked={effectiveShowWarehouseWhiskers}
                onChange={(event) => handleShowWarehouseWhiskersChange(event.currentTarget.checked)}
              />
              <span>Show warehouse whiskers</span>
            </label>
          )}
          <LibraryViewToggle
            value={mode}
            ariaLabel="Event calendar mode"
            options={[
              { value: 'month', label: 'Month' },
              { value: 'week', label: 'Week' },
              { value: 'day', label: 'Day' },
            ]}
            onChange={(nextMode) => {
              if (selectedDay) {
                setAnchor(selectedDay);
                setSelectedDay(null);
              }
              setMode(nextMode);
            }}
          />
          {isPhone ? (
            <MenuButton
              className="btn xs btn-outline cal-toolbar__more"
              ariaLabel="Calendar options"
              menuLabel="Calendar options"
              align="right"
              items={optionItems}
            >
              <span aria-hidden="true">⋯</span>
            </MenuButton>
          ) : null}
        </div>
      </div>

      {/* Calendar body */}
      {mode === 'month' && (
        <MonthView events={calEvents} anchor={anchor} tooltip={tooltip} onHover={handleHover} overdueMap={overdueMap} onDayClick={handleDayClick} onDayDblClick={handleDayDblClick} selectedDay={selectedDay} availabilityMap={availabilityMap} availMode={availabilityMode} onEventContextMenu={handleEventContextMenu} />
      )}
      {mode === 'week' && (
        <WeekView events={calEvents} anchor={anchor} tooltip={tooltip} onHover={handleHover} overdueMap={overdueMap} onDayClick={handleDayClick} onDayDblClick={handleDayDblClick} selectedDay={selectedDay} availabilityMap={availabilityMap} availMode={availabilityMode} onEventContextMenu={handleEventContextMenu} />
      )}
      {mode === 'day' && (
        <DayView events={calEvents} anchor={anchor} tooltip={tooltip} onHover={handleHover} overdueMap={overdueMap} availabilityMap={availabilityMap} availMode={availabilityMode} onEventContextMenu={handleEventContextMenu} />
      )}

      {/* Context menu (right-click on events) */}
      {contextMenu && (
        <CalendarContextMenu
          ctx={contextMenu}
          onClose={closeContextMenu}
          onDelete={onDelete}
          onStageUpdated={onStageUpdated}
          onBeginPicking={onBeginPicking}
        />
      )}

      {/* Legend: folded to one row on phones */}
      {isPhone ? (
        <details className="cal-legend cal-legend--folded">
          <summary className="cal-legend__summary">
            <span className="cal-legend__summary-title">Stage icons</span>
            <span className="cal-legend__summary-count">{stageLegend.length} stages</span>
          </summary>
          {legendItems}
        </details>
      ) : (
        <div className="cal-legend">
          <span className="cal-legend__title">Stage Legend</span>
          {legendItems}
        </div>
      )}
    </div>
  );
}
